import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { jobsAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Pencil, Users, Briefcase, ArrowRight } from 'lucide-react';

const Field = ({ label, value, showAll, testId }) => {
  const empty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  if (empty && !showAll) return null;
  return (
    <div data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{empty ? '—' : value}</p>
    </div>
  );
};

const Section = ({ title, children }) => {
  const hasContent = React.Children.toArray(children).some(Boolean);
  if (!hasContent) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </div>
  );
};

export default function ClientViewDialog({ client, open, onOpenChange, onEdit, canEdit }) {
  const [showAll, setShowAll] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  useEffect(() => {
    if (!open || !client?.id) { setJobs([]); return; }
    let cancelled = false;
    setJobsLoading(true);
    jobsAPI.list({ client_id: client.id })
      .then(res => { if (!cancelled) setJobs(res.data || []); })
      .catch(() => { if (!cancelled) setJobs([]); })
      .finally(() => { if (!cancelled) setJobsLoading(false); });
    return () => { cancelled = true; };
  }, [open, client?.id]);

  if (!client) return null;
  const c = client;
  const cityStateZip = [c.city, [c.state, c.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const primaryContact = [c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ');
  const contacts = c.additional_contacts || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-view-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4 pr-8">
            <div>
              <DialogTitle className="text-xl">{c.name}</DialogTitle>
              <DialogDescription className="sr-only">Client detail view</DialogDescription>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="outline" className="font-mono text-xs" data-testid="client-view-customer-number">{c.customer_number}</Badge>
                <Badge className={c.tax_status === 'exempt' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'}>
                  {c.tax_status === 'exempt' ? 'Tax Exempt' : 'Taxable'}
                </Badge>
                {!c.is_active && <Badge variant="secondary">Inactive</Badge>}
              </div>
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={onEdit} data-testid="client-view-edit-btn">
                <Pencil className="w-4 h-4 mr-1.5" /> Edit
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex items-center justify-end gap-2 -mt-2">
          <Label htmlFor="client-show-all" className="text-xs text-slate-500">Show all fields</Label>
          <Switch id="client-show-all" checked={showAll} onCheckedChange={setShowAll} data-testid="client-view-show-all-toggle" />
        </div>

        <div className="space-y-5">
          <Section title="Client">
            <Field label="Customer Number" value={c.customer_number} showAll={showAll} testId="client-view-cn" />
            <Field label="Date Entered" value={c.date_entered} showAll={showAll} testId="client-view-date-entered" />
            <Field label="Terms" value={c.terms} showAll={showAll} testId="client-view-terms" />
            {(c.tax_status === 'exempt' || showAll) && (
              <Field label="Tax Exempt ID" value={c.tax_exempt_id} showAll={showAll} testId="client-view-tax-exempt-id" />
            )}
            {(c.winteam_customer_numbers || []).length > 1 && (
              <div className="sm:col-span-2" data-testid="client-view-wt-numbers">
                <p className="text-xs uppercase tracking-wider text-slate-400">WinTeam Customer Number(s)</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {c.winteam_customer_numbers.map((n) => (
                    <Badge key={n} variant="outline" className="font-mono text-xs">{n}</Badge>
                  ))}
                </div>
              </div>
            )}
          </Section>

          <Section title="Address">
            <Field label="Address 1" value={c.address1} showAll={showAll} testId="client-view-address1" />
            <Field label="Address 2" value={c.address2} showAll={showAll} testId="client-view-address2" />
            <Field label="City / State / Zip" value={cityStateZip} showAll={showAll} testId="client-view-csz" />
          </Section>

          <Section title="Primary Contact">
            <Field label="Name" value={primaryContact} showAll={showAll} testId="client-view-contact-name" />
            <Field label="Attention" value={c.attention} showAll={showAll} testId="client-view-attention" />
            <Field label="Phone" value={c.phone} showAll={showAll} testId="client-view-phone" />
          </Section>

          {(contacts.length > 0 || showAll) && (
            <div data-testid="client-view-additional-contacts">
              <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" />
                Additional Contacts / Invoice Recipients ({contacts.length})
              </h4>
              {contacts.length === 0 ? (
                <p className="text-sm text-slate-400">None</p>
              ) : (
                <div className="space-y-2">
                  {contacts.map((ct, i) => (
                    <div key={ct.id || i} className="p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`client-view-contact-${i}`}>
                      <p className="text-sm font-medium text-slate-800">
                        {[ct.first_name, ct.last_name].filter(Boolean).join(' ') || '(unnamed contact)'}
                      </p>
                      <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                        {ct.email && <p data-testid={`client-view-contact-${i}-email`}>{ct.email}</p>}
                        {(ct.phone1 || ct.phone2) && <p>{[ct.phone1, ct.phone2].filter(Boolean).join(' · ')}</p>}
                        {(ct.address || ct.city) && (
                          <p>{[ct.address, [ct.city, [ct.state, ct.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', ')}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(c.notes || showAll) && (
            <div data-testid="client-view-notes">
              <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-2">Notes</h4>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{c.notes || '—'}</p>
            </div>
          )}

          <div data-testid="client-view-jobs">
            <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-slate-400" />
              Jobs ({jobs.length})
              <Link
                to={`/jobs?client=${c.id}`}
                className="ml-auto text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                onClick={() => onOpenChange(false)}
                data-testid="client-view-jobs-open-list"
              >
                Open jobs list <ArrowRight className="w-3 h-3" />
              </Link>
            </h4>
            {jobsLoading ? (
              <p className="text-sm text-slate-400">Loading jobs…</p>
            ) : jobs.length === 0 ? (
              <p className="text-sm text-slate-400">No jobs on file yet.</p>
            ) : (
              <div className="space-y-1.5">
                {jobs.slice(0, 8).map(j => (
                  <div key={j.id} className="flex items-center gap-3 p-2 rounded bg-slate-50 border border-slate-100 text-sm">
                    <Badge variant="outline" className="font-mono text-xs">{j.job_number}</Badge>
                    <span className="flex-1 truncate">{j.job_description || '(no description)'}</span>
                    {!j.is_active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                  </div>
                ))}
                {jobs.length > 8 && (
                  <p className="text-xs text-slate-400 pl-2">+ {jobs.length - 8} more — open the jobs list to see all.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
